import { GlobalStore } from '@roc/feature-app-core';
import { isSecureLinkError } from '@roc-capital/secure-link';
import { makeObservable, override, action, observable, computed, flow } from 'mobx';
import { PrivatePlaidService } from '../../../../apps/client-portal-public/src/app/services/privatePlaidService';
import {
  PLAID_REASON,
  PlaidConnectResult,
  SecureLinkAchService,
  SecureLinkRequest,
} from '../services/secureLinkAchService';
import { AchFormBaseStore } from './achFormStore';
import { PlaidBankInformationFormStore } from './plaidBankInformationFormStore';

const DOCUSIGN_STEP = 1;
const SUMMARY_STEP = 2;

/** Survives the bank's OAuth redirect (same tab): Plaid Link must re-open with the same token. */
const PLAID_LINK_TOKEN_KEY = 'plp.ach.plaidLinkToken';

/** Below this, warn before opening Plaid: a bank login (with OAuth) can take a few minutes. */
export const PLAID_SESSION_WARNING_SECONDS = 180;

/** The account picked in Plaid Link (from Link's metadata): shown before Continue, never contains numbers. */
export interface ConnectedPlaidAccount {
  publicToken: string;
  accountId: string;
  institutionId?: string;
  institutionName?: string;
  mask?: string;
  subtype?: string;
}

const noSession: SecureLinkRequest = () =>
  Promise.reject(new Error('SecureLinkAchFormStore: call setRequest() with the session request first'));

const readStoredLinkToken = (): string | null => {
  try {
    return window.sessionStorage.getItem(PLAID_LINK_TOKEN_KEY);
  } catch {
    return null;
  }
};

const storeLinkToken = (token: string | null) => {
  try {
    if (token) {
      window.sessionStorage.setItem(PLAID_LINK_TOKEN_KEY, token);
    } else {
      window.sessionStorage.removeItem(PLAID_LINK_TOKEN_KEY);
    }
  } catch {
    // Without storage the flow still works; only an OAuth bank redirect would need to start Plaid again.
  }
};

/** Returning from a bank's OAuth login to /link/ach. */
export const isPlaidOAuthReturn = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).has('oauth_state_id');
  } catch {
    return false;
  }
};

/**
 * The ACH form store for the secure link flow (/link/ach). Same screens and steps as AchFormStore; the calls go to
 * vendor-service's session-guarded endpoints (SecureLinkAchService) instead of the signed-in ones.
 *
 * Bank step: "Connect your bank" through Plaid when the link data says plaidEnabled (connect first, then the account
 * name and payment day; one submit at Continue), with the manual form as fallback when manualEntryEnabled.
 *
 * Created with the app's root store, before there is a session: the page passes the session's request in with
 * setRequest() once useSecureLinkSession has one. Until then every call rejects.
 */
export class SecureLinkAchFormStore extends AchFormBaseStore {
  private achService: SecureLinkAchService;

  plaidBankInformationFormStore: PlaidBankInformationFormStore;
  /** The borrower chose (or was sent to) the manual form. */
  public manualChosen = false;
  public plaidLinkToken: string = null;
  public plaidLinkTokenLoading = false;
  public connectedAccount: ConnectedPlaidAccount = null;
  public plaidSubmitting = false;
  /** What vendor-service linked (shown on later steps if needed). */
  public linkedAccount: PlaidConnectResult = null;
  /** Seconds left in the secure link session (from useSecureLinkSession). */
  public secondsLeft: number = null;

  constructor(globalStore: GlobalStore) {
    super(globalStore);
    this.plaidBankInformationFormStore = new PlaidBankInformationFormStore(globalStore);
    this.setRequest(noSession);
    makeObservable(this, {
      setRequest: action,
      getACHData: override,
      generateDocument: override,
      disableStartACHForm: override,
      usePlaidStep: override,
      canSwitchToPlaid: override,
      switchToPlaid: override,
      manualChosen: observable,
      plaidLinkToken: observable,
      plaidLinkTokenLoading: observable,
      connectedAccount: observable,
      plaidSubmitting: observable,
      linkedAccount: observable,
      secondsLeft: observable,
      plaidAvailable: computed,
      manualLink: computed,
      manualAvailable: computed,
      sessionEndingSoon: computed,
      chooseManual: action,
      onPlaidSuccess: action,
      changeConnectedAccount: action,
      setSecondsLeft: action,
      fetchPlaidLinkToken: flow,
      submitPlaid: flow,
    });
  }

  /** The authorized fetch from useSecureLinkSession; every call goes through it. */
  setRequest(request: SecureLinkRequest) {
    this.achService = new SecureLinkAchService(request);
    // The base store's bank step calls sentBofaValidation and saveBankInformation, which SecureLinkAchService provides.
    this.plaidService = (this.achService as unknown) as PrivatePlaidService;
  }

  *getACHData(_ignored?: string) {
    return yield this.achService.getACHData();
  }

  get plaidAvailable(): boolean {
    return this.achData?.plaidEnabled === true;
  }

  get manualAvailable(): boolean {
    return this.achData?.manualEntryEnabled !== false;
  }

  /** A manual-entry link: typed bank details only, even on a loan where Plaid is on. */
  get manualLink(): boolean {
    return this.achData?.entryMode === 'MANUAL';
  }

  get usePlaidStep(): boolean {
    return this.plaidAvailable && !this.manualLink && !(this.manualChosen && this.manualAvailable);
  }

  get canSwitchToPlaid(): boolean {
    return this.plaidAvailable && !this.manualLink && this.manualChosen;
  }

  get sessionEndingSoon(): boolean {
    return this.secondsLeft != null && this.secondsLeft > 0 && this.secondsLeft < PLAID_SESSION_WARNING_SECONDS;
  }

  setSecondsLeft(secondsLeft: number) {
    this.secondsLeft = secondsLeft;
  }

  switchToPlaid() {
    this.manualChosen = false;
    this.bankStepNotice = null;
  }

  /** To the manual form, keeping what was already typed (account name, payment day). */
  chooseManual(notice: string = null) {
    if (!this.manualAvailable) {
      // A Plaid-only link has no manual form: show why, and let the borrower pick another account (or ask for a manual link).
      this.connectedAccount = null;
      this.bankStepNotice = notice;
      return;
    }
    const values = this.plaidBankInformationFormStore.getFormValues();
    if (values.withdrawalDay) {
      this.bankInformationFormStore.onFieldChange('withdrawalDay', values.withdrawalDay);
    }
    this.connectedAccount = null;
    this.manualChosen = true;
    this.bankStepNotice = notice;
  }

  /** A Plaid Link token for this session; reused after a bank OAuth redirect. */
  *fetchPlaidLinkToken() {
    if (this.plaidLinkToken || this.plaidLinkTokenLoading) return;
    const stored = isPlaidOAuthReturn() ? readStoredLinkToken() : null;
    if (stored) {
      this.plaidLinkToken = stored;
      return;
    }
    this.plaidLinkTokenLoading = true;
    try {
      const token: string = yield this.achService.getPlaidLinkToken();
      this.plaidLinkToken = token;
      storeLinkToken(token);
    } catch (error) {
      this.handlePlaidError(error, "We couldn't start the bank connection.");
    } finally {
      this.plaidLinkTokenLoading = false;
    }
  }

  /** Plaid Link finished: keep what's needed to submit at Continue (nothing is saved yet). */
  onPlaidSuccess(publicToken: string, metadata: any) {
    const account = metadata?.accounts?.[0] ?? metadata?.account;
    this.connectedAccount = {
      publicToken,
      accountId: account?.id ?? metadata?.account_id,
      institutionId: metadata?.institution?.institution_id,
      institutionName: metadata?.institution?.name,
      mask: account?.mask,
      subtype: account?.subtype,
    };
    this.bankStepNotice = null;
  }

  /** "Change": pick another account (opens Plaid Link again). */
  changeConnectedAccount() {
    this.connectedAccount = null;
  }

  /** Continue: one call links the account and saves the step; then on to signing. */
  *submitPlaid() {
    if (this.plaidSubmitting) return;
    if (!this.connectedAccount?.publicToken || !this.connectedAccount?.accountId) {
      this.globalStore.notificationStore.showErrorNotification({ message: 'Please connect your bank account first.' });
      return;
    }
    this.plaidBankInformationFormStore.runFormValidationWithMessage();
    if (!this.plaidBankInformationFormStore.form.meta.isValid) return;

    const values = this.plaidBankInformationFormStore.getFormValues();
    this.plaidSubmitting = true;
    try {
      const linked: PlaidConnectResult = yield this.achService.connectPlaidAccount({
        publicToken: this.connectedAccount.publicToken,
        accountId: this.connectedAccount.accountId,
        institutionId: this.connectedAccount.institutionId,
        withdrawalDay: values.withdrawalDay,
      });
      this.linkedAccount = linked;
      storeLinkToken(null);
      this.bankStepNotice = null;
      this.activeStep = DOCUSIGN_STEP;
    } catch (error) {
      this.handlePlaidError(error, 'Error while connecting your bank account. Please try again.');
    } finally {
      this.plaidSubmitting = false;
    }
  }

  private handlePlaidError(error: any, fallback: string) {
    if (!isSecureLinkError(error)) {
      this.globalStore.notificationStore.showErrorNotification({ message: fallback });
      return;
    }
    const reason = (error.details as any)?.reason;
    if (error.kind === 'INVALID') {
      return; // The page switches to "session ended".
    }
    if (error.kind === 'COMPLETED') {
      this.activeStep = SUMMARY_STEP;
      return;
    }
    if (reason === PLAID_REASON.MANUAL_ENTRY_REQUIRED || reason === PLAID_REASON.PLAID_DISABLED) {
      this.chooseManual(error.message);
      return;
    }
    if (reason === PLAID_REASON.RECONNECT_REQUIRED) {
      this.connectedAccount = null;
      this.bankStepNotice = error.message;
      return;
    }
    if (error.kind === 'UNAVAILABLE' && this.manualAvailable) {
      this.chooseManual(error.message || fallback);
      return;
    }
    this.globalStore.notificationStore.showErrorNotification({ message: error.message || fallback });
  }

  /** A second submit (409) means the form is already signed: show the summary rather than an error. */
  *generateDocument(signature): Generator<unknown, void, unknown> {
    try {
      yield this.achService.generateAchDocument({ signature, userAgent: navigator.userAgent });
      this.activeStep = SUMMARY_STEP;
    } catch (error) {
      if (isSecureLinkError(error) && error.kind === 'COMPLETED') {
        this.activeStep = SUMMARY_STEP;
        return;
      }
      if (isSecureLinkError(error) && error.kind === 'INVALID') {
        // The page switches to "session ended"; no toast needed.
        return;
      }
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while generating the ACH document. Please try again.',
      });
    }
  }

  /** The link is for one borrower, so there is no borrower id to wait for. */
  get disableStartACHForm() {
    return !this.achData;
  }
}
